import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { campaignActions } from '../../actions/campaign';

const formatCount = (count) => {
  const num = parseFloat(count);
  if (isNaN(num)) return count || '—';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  if (num <= 1 && num > 0) return num.toFixed(2);
  return String(Math.round(num));
};

const getStatusStyle = (status, is_live) => {
  if (is_live) return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Live' };
  switch (status) {
    case 'completed': return { bg: 'bg-green-50', text: 'text-green-700', label: 'Completed' };
    case 'paused': return { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Paused' };
    case 'draft': return { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Draft' };
    default: return { bg: 'bg-gray-100', text: 'text-gray-500', label: status || 'Unknown' };
  }
};

function MetricCell({ value, label, icon }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center mb-1">
        {icon}
      </div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
      <div className="text-[10px] text-gray-400 font-medium">{label}</div>
    </div>
  );
}

function PlatformSection({ platform, icon, iconColor, bgColor, borderColor, budget, metrics, isDraft }) {
  return (
    <div className={`${bgColor} ${borderColor} border rounded-xl p-4 mt-3`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg ${bgColor} flex items-center justify-center`}>
          {icon}
        </div>
        <span className="font-bold text-sm text-gray-900">{platform}</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-9 gap-3">
        <MetricCell value={`Rs. ${budget}`} label="Budget" icon={<svg className="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" /><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" /></svg>} />
        {metrics.map((m, i) => (
          <MetricCell key={i} value={isDraft ? '—' : m.value} label={m.label} icon={m.icon} />
        ))}
      </div>
    </div>
  );
}

function SparkleList({ campaigns, loading, allowedSocailMediaLinks, getCampaigns }) {
  const history = useHistory();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getCampaigns(); }, []);

  const handleClick = (campaign) => {
    const defaultPlatform = (campaign.id === 137 || (campaign.platform?.length > 0 && campaign.platform?.includes('instagram')))
      ? 'instagram' : 'youtube';
    history.push(`/brand/campaigns/${campaign.id}/dashboard/${defaultPlatform}`);
  };

  const showYoutube = (c) =>
    allowedSocailMediaLinks?.includes('youtube') && (c.platform?.length === 0 || c.platform?.includes('youtube'));
  const showInstagram = (c) =>
    allowedSocailMediaLinks?.includes('instagram') && (c.platform?.length === 0 || c.platform?.includes('instagram'));
  const isDraft = (c) => !c.is_live && c.status === 'draft';

  if (loading) {
    return (
      <div>
        <div className="flex justify-center items-center py-20">
          <div className="w-10 h-10 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Campaigns</h1>
            <p className="text-gray-400 text-sm mt-1">Manage and monitor all brand campaigns across platforms</p>
          </div>
        </div>

        {/* Campaign Grid */}
        {!campaigns || campaigns.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
            <p className="text-gray-500 font-medium">No campaigns found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {campaigns.map((c) => {
              const ss = getStatusStyle(c.status, c.is_live);
              const draft = isDraft(c);

              return (
                <div
                  key={c.id}
                  onClick={() => handleClick(c)}
                  className={`group bg-white rounded-2xl border border-gray-100 hover:shadow-soft-lg hover:border-gray-200 transition-all duration-200 cursor-pointer hover:-translate-y-1 ${draft ? 'opacity-60' : ''}`}
                >
                  {/* Card Header */}
                  <div className="p-5 pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-soft-md">
                          {(c.name || 'C')[0]}
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900 leading-tight line-clamp-1 group-hover:text-amber-600 transition-colors">
                            {c.name}
                          </h3>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {c.objective ? `Objective: ${c.objective}` : 'No objective set'}
                          </p>
                        </div>
                      </div>
                      <span className={`${ss.bg} ${ss.text} text-[11px] font-semibold px-2.5 py-0.5 rounded-md`}>
                        {ss.label}
                      </span>
                    </div>

                    {c.desc && (
                      <p className="text-xs text-gray-400 line-clamp-2 mt-1">{c.desc}</p>
                    )}
                  </div>

                  {/* Platform Sections */}
                  <div className="px-5 pb-4">
                    {showYoutube(c) && (
                      <PlatformSection
                        platform="YouTube"
                        icon={<svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>}
                        bgColor="bg-red-50"
                        borderColor="border-red-100"
                        budget={c.budget}
                        isDraft={draft}
                        metrics={[
                          { value: formatCount(c.total_posts), label: 'Videos', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> },
                          { value: formatCount(c.total_views), label: 'Views', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> },
                          { value: formatCount(c.total_likes), label: 'Likes', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg> },
                          { value: formatCount(c.total_comments), label: 'Comments', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg> },
                          { value: c.total_engagement_rate || '—', label: 'Eng. Rate', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> },
                          { value: c.duration ? `${c.duration}d` : '—', label: 'Duration', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                          { value: c.start_date || '—', label: 'Start', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                          { value: c.end_date || '—', label: 'End', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                        ]}
                      />
                    )}

                    {showInstagram(c) && (
                      <PlatformSection
                        platform="Instagram"
                        icon={<svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>}
                        bgColor="bg-pink-50"
                        borderColor="border-pink-100"
                        budget={c.budget}
                        isDraft={draft}
                        metrics={[
                          { value: formatCount(c.combined_posts ?? c.ig_total_posts), label: 'Posts', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                          { value: formatCount(c.ig_total_views), label: 'Views', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> },
                          { value: formatCount(c.ig_total_likes), label: 'Engagement', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg> },
                          { value: formatCount(c.ig_total_impression), label: 'Impressions', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> },
                          { value: formatCount(c.ig_total_reach), label: 'Reach', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg> },
                          { value: c.ig_total_engagement_rate || '—', label: 'Eng. Rate', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> },
                          { value: c.start_date || '—', label: 'Start', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                          { value: c.end_date || '—', label: 'End', icon: <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                        ]}
                      />
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="px-5 py-3 border-t border-gray-50 flex justify-end gap-2">
                    <button
                      className="text-xs font-medium text-gray-500 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleClick(c); }}
                    >
                      View Details
                    </button>
                    <button
                      className="text-xs font-medium text-white px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 shadow-soft transition-all"
                      onClick={(e) => { e.stopPropagation(); handleClick(c); }}
                    >
                      Analytics
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  loading: state.campaign.loading,
  campaigns: state.campaign.campaigns,
  allowedSocailMediaLinks: state.brand.displayPanels?.social_media?.link_display || [],
});

export default connect(mapStateToProps, { getCampaigns: campaignActions.getCampaigns })(SparkleList);
